// © 2015-2021 WDNA - All Rights Reserved
import _ from 'lodash';

import { UserDO } from 'wdna-components/lib/types.new';

import history from '../../store/history';
import {
  AUTH_USER,
  REMOVE_USER
} from './actions';

export default class Auth {
  /**
   * Refresh user logged information on state and api-token
   *
   * @param user user object
   */
  public static updateUser(user: UserDO | null): void {
    // Important to access backend
    window.api.setToken(Auth.getToken() || '');
    window.store.dispatch({
      type: AUTH_USER,
      user,
    });

    // window.StartupService.enqueueTasks();
  }

  /**
   * Authenticate a user. Save a token string in Storage
   *
   * @param user user object
   */
  public static authenticateUser(user: UserDO | null): void {
    if (user && user.token) {
      Auth.setUser(user);
      Auth.setToken(user.token);
      Auth.updateUser(user);
    }
  }

  /**
   * Check if a user is authenticated - check if a token is saved in Storage
   *
   * @returns true if user is authenticated false otherwise
   */
  public static isUserAuthenticated(): boolean {
    return Auth.getToken() !== null;
  }

  /**
   * Deauthenticate a user. Remove a token from Storage.
   *
   * @param clear indicates if session storage must be cleared
   */
  public static deauthenticateUser(clear: boolean = false): void {
    if (window.CacheService) window.CacheService.reset();

    // localStorage.removeItem('count');
    localStorage.removeItem('user');
    localStorage.removeItem('token');

    if (clear) sessionStorage.clear();

    // TODO: fixe user redux
    window.store.dispatch({
      type: REMOVE_USER,
    });

    // Redirect to login window
    setTimeout(() => {
      history.replace('/login');
    }, 0);
  }

  /**
   * Save a token string in Storage
   *
   * @param token user api token
   */
  private static setToken(token: string): void {
    localStorage.setItem('token', token);
    sessionStorage.setItem('token', token);
  }

  /**
   * Get a token value.
   *
   * @returns with the user token
   */
  public static getToken(): string | null {
    return localStorage.getItem('token') ? localStorage.getItem('token') : sessionStorage.getItem('token');
  }

  /**
   * Get access value.
   *
   * @returns with the access list
   */
  public static getAccess(): Record<string, string> | null {
    const access: string | null = sessionStorage.getItem(`access_${window.APP_CODE}`);
    return access ? JSON.parse(access) : null;
  }

  /**
   * Save a user info in Storage
   *
   * @param user User info object to save
   */
  private static setUser(user: UserDO): void {
    if (user) {
      const value: string = JSON.stringify(_.omit(user, ['access']));
      localStorage.setItem('user', value);
      sessionStorage.setItem('user', value);
      sessionStorage.setItem(`access_${window.APP_CODE}`, JSON.stringify(user.access));
    }
  }

  /**
   * Get the current user information in the storage
   *
   * @returns With the user information or null
   */
  public static getUser(): UserDO | null {
    const json: string | null = localStorage.getItem('user') ? localStorage.getItem('user') : sessionStorage.getItem('user');
    const user: UserDO | null = json ? JSON.parse(json) as UserDO : null;
    if (user != null) user.access = JSON.parse(sessionStorage.getItem(`access_${window.APP_CODE}`) || '{}');

    return user;
  }

  /**
   * Save the browser timezone into storage
   */
  public static setTimeZone(): void {
    localStorage.setItem('timezone', Intl.DateTimeFormat().resolvedOptions().timeZone);
  }

  /**
   * Get a browser time-zone value.
   *
   * @returns time-zone name of the browser
   */
  public static getTimeZone(): string | null {
    return localStorage.getItem('timezone');
  }

  /**
   * Function to check authentication on close browser window/tab
   *
   * @param load indicates if is load tab/window or not
   */
  public static chkAuthFront(load: boolean): void {
    const def: number = (load ? 0 : 1);
    const inc: number = (load ? +1 : -1);

    const oldCount: string | null = localStorage.getItem('count');
    const newCount: number = (oldCount ? parseInt(oldCount, 10) : def) + inc;
    localStorage.setItem('count', `${newCount}`);

    // To avoid desconnection on reload page
    if (load && sessionStorage.getItem('token') != null && localStorage.getItem('token') == null) {
      localStorage.setItem('token', sessionStorage.getItem('token') || '');
      localStorage.setItem('user', sessionStorage.getItem('user') || '');
    }
    if (!load && sessionStorage.getItem('token') == null && localStorage.getItem('token') != null) {
      sessionStorage.setItem('token', localStorage.getItem('token') || '');
      sessionStorage.setItem('user', localStorage.getItem('user') || '');
    }

    if (!load && newCount < 1) {
      this.deauthenticateUser();
    }
  }
}
